import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';

const CAPABILITIES = [
  {
    title: 'Appointment & Scheduling Systems',
    description:
      'Replace paper-based booking with digital scheduling for councils, hospitals, and service centres.',
  },
  {
    title: 'Citizen-Service Portals',
    description:
      'Give citizens a single online point of access to apply for permits, check status, and communicate with public services.',
  },
  {
    title: 'Application & Approval Workflows',
    description:
      'Digitise internal approval processes — procurement, permits, grants — with automated routing and tracking.',
  },
  {
    title: 'Digital Records & Document Management',
    description:
      'Replace physical filing systems with searchable, secure digital records that authorised staff can access immediately.',
  },
  {
    title: 'Internal Staff Systems',
    description:
      'Build tools that help public-sector teams manage their work, track performance, and collaborate more effectively.',
  },
  {
    title: 'Project-Monitoring Dashboards',
    description:
      'Give decision-makers real-time visibility into project progress, budgets, and outcomes.',
  },
  {
    title: 'Public-Service Reporting Tools',
    description:
      'Generate automated reports on service delivery, response times, and citizen satisfaction.',
  },
  {
    title: 'Custom Prototypes & Proofs of Concept',
    description:
      'Test a solution before committing to a full build. We’ll design and demonstrate a working prototype tailored to your specific requirement.',
  },
] as const;

const DEMO_CTA = '/contact?subject=Request%20a%20Demonstration';

const GovernmentSolutions: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="w-full bg-white">
      <section className="bg-white pb-10 pt-12 md:pt-16">
        <div className="layout-container mx-auto max-w-[1360px] px-5">
          <div className="max-w-[720px]">
            <Badge className="inline-flex rounded-full border border-[#B8B8B8] bg-white px-3 py-1 font-description text-[10px] font-normal text-[#9C9C9C]">
              Government Solutions
            </Badge>
            <h1 className="mt-3 font-title text-[32px] font-semibold leading-[1.08] tracking-[-0.02em] text-[#212121] md:text-[40px] lg:text-[46px]">
              Solutions for Government &amp; Public Institutions
            </h1>
            <p className="mt-4 font-description text-[13px] font-light leading-[1.55] text-[#919191] md:text-[14px]">
              We help public institutions explore and implement practical digital solutions that improve how
              they serve citizens and manage operations.
            </p>
            <p className="mt-4 font-description text-[13px] font-light leading-[1.55] text-[#919191] md:text-[14px]">
              Public institutions face unique challenges — legacy systems, manual processes, and pressure to
              deliver better services with limited resources. We design and build digital solutions that
              address these challenges directly, without requiring a complete overhaul of existing
              infrastructure.
            </p>
            <p className="mt-4 font-description text-[13px] font-light leading-[1.55] text-[#919191] md:text-[14px]">
              We are currently working with public-sector organizations to understand their needs and develop
              solutions that can scale. Whether you’re exploring a pilot project or ready to implement a full
              system, we’d welcome the opportunity to discuss how we can support your digital transformation
              journey.
            </p>
            <Link to={DEMO_CTA} className="mt-6 inline-block">
              <Button className="rounded-[8px] bg-[#2776EA] px-6 py-2.5 font-description text-[13px] font-medium text-white hover:bg-blue-600 transition-colors">
                Request a Demonstration
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-[#F8FAFE] py-14 md:py-16">
        <div className="layout-container mx-auto max-w-[1360px] px-5">
          <h2 className="font-title text-[28px] font-medium leading-[1.1] text-[#212121] md:text-[36px]">
            Our Capabilities
          </h2>

          <div className="mt-10 grid grid-cols-1 gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
            {CAPABILITIES.map((capability, index) => (
              <div key={capability.title} className="flex flex-col gap-2">
                <span className="font-title text-[11px] font-medium uppercase tracking-wider text-[#2776EA]">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h3 className="font-title text-[16px] font-medium leading-tight text-[#212121] md:text-[17px]">
                  {capability.title}
                </h3>
                <p className="font-description text-[12px] font-light leading-[1.55] text-[#919191] md:text-[13px]">
                  {capability.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-14 md:py-16">
        <div className="layout-container mx-auto max-w-[800px] px-5 text-center">
          <h2 className="font-title text-[28px] font-medium text-[#212121] md:text-[34px]">
            Ready to see it in action?
          </h2>
          <p className="mt-3 font-description text-[13px] font-light text-[#919191] md:text-[14px]">
            Request a demonstration and we’ll walk you through how these solutions can support your
            institution.
          </p>
          <Link to={DEMO_CTA} className="mt-6 inline-block">
            <Button className="rounded-[8px] bg-[#2776EA] px-6 py-2.5 font-description text-[13px] font-medium text-white hover:bg-blue-600 transition-colors">
              Request a Demonstration
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default GovernmentSolutions;
